import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Theme } from "@mui/material";

import { Tooltip } from "./Tooltip";

/**
 * What a state means to the user, which decides the icon colour:
 *
 * - `positive`: on, active, fine.
 * - `neutral`: off, waiting or not applicable. Not a fault, so never red.
 * - `warning`: something is weird and worth a look.
 * - `error`: something is broken or no longer works.
 */
export type StatusTone = "positive" | "neutral" | "warning" | "error";

const defaultIcons: Record<StatusTone, string> = {
    positive: "fa-solid fa-circle-check",
    neutral: "fa-regular fa-circle",
    warning: "fa-solid fa-circle-exclamation",
    error: "fa-solid fa-circle-exclamation",
};

const useStyles = makeStyles<{ tone: StatusTone }>()((theme: Theme, { tone }) => ({
    root: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    icon: {
        color: {
            positive: theme.palette.emteria.success,
            neutral: theme.palette.emteria.indicator,
            warning: theme.palette.emteria.warning,
            error: theme.palette.emteria.error,
        }[tone],
    },
}));

type Props = {
    tone: StatusTone;
    /**
     * Font Awesome classes for a state that has an icon of its own (a battery level, a connection type). Leave it out
     * to use the tone's default icon.
     */
    icon?: string;
    tooltip?: React.ReactNode;
    children: React.ReactNode;
};

/**
 * A state shown as a coloured icon followed by its text. The text keeps the surrounding colour, so the label also
 * works inside a `TextLink`.
 */
export const StatusLabel = ({ tone, icon, tooltip, children }: Props) => {
    const { classes, cx } = useStyles({ tone });

    const label = (
        <span className={classes.root}>
            <i className={cx(icon ?? defaultIcons[tone], classes.icon)} aria-hidden />
            <span>{children}</span>
        </span>
    );

    return tooltip ? <Tooltip title={tooltip}>{label}</Tooltip> : label;
};

/**
 * Whether a setting, service or add-on is switched on.
 */
export const EnabledStatusLabel = ({ enabled }: { enabled: boolean }) => {
    const { t } = useTranslation(["common"]);

    return enabled ? (
        <StatusLabel tone="positive">{t("common:label.enabled")}</StatusLabel>
    ) : (
        <StatusLabel tone="neutral">{t("common:label.disabled")}</StatusLabel>
    );
};

/**
 * Something the user started that is waiting on someone else, such as an invitation nobody has accepted yet. Icon
 * only, for a table row where the item's name already sits next to it, so the tooltip has to say what is pending.
 */
export const PendingStatusIcon = ({ tooltip }: { tooltip: React.ReactNode }) => {
    const { t } = useTranslation(["common"]);
    const { classes, cx } = useStyles({ tone: "neutral" });

    return (
        <Tooltip title={tooltip}>
            <i className={cx("fa-regular fa-clock", classes.icon)} role="img" aria-label={t("common:status.pending")} />
        </Tooltip>
    );
};
